// Soft three-note chime (Web Audio, no file) + gentle vibration for "new counter offer".
// Browsers block audio until the user has interacted with the page — we just no-op then.
const recent = new Map();

// `key` de-dupes the same offer heard via two channels (live socket + trip poll) within 20 s.
export function playSoftChime(key) {
  if (key) {
    const t = Date.now();
    if (t - (recent.get(key) || 0) < 20000) return;
    recent.set(key, t);
  }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [784, 988, 1175].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = now + i * 0.12;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.5);
      });
      setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 1100);
    }
  } catch (_) { /* noop */ }
  try {
    const pref = localStorage.getItem("yorm_haptics_enabled");
    if (pref !== "false" && navigator.vibrate) navigator.vibrate([30, 50, 30]);
  } catch (_) { /* noop */ }
}

// Repeat the chime + vibration every `everyMs` until `untilMs` (epoch ms) or stop() is called.
export function startRinging({ untilMs, everyMs = 3000 }) {
  let timer = null;
  const ring = () => {
    if (Date.now() >= untilMs) { stop(); return; }
    playSoftChime();
  };
  function stop() { if (timer) { clearInterval(timer); timer = null; } }
  ring();
  timer = setInterval(ring, everyMs);
  return stop;
}

// Soft "tick" (like iOS keyboard) + light vibration for tile taps. One shared AudioContext.
let tapCtx = null;
export function playTapClick() {
  let soundOn = true;
  try { soundOn = localStorage.getItem("yorm_tap_sounds_enabled") !== "false"; } catch (_) { /* noop */ }
  try {
    const Ctx = soundOn && (window.AudioContext || window.webkitAudioContext);
    if (Ctx) {
      tapCtx = tapCtx || new Ctx();
      if (tapCtx.state === "suspended") tapCtx.resume();
      const now = tapCtx.currentTime;
      // Soft, rounded "pop" like iOS taps: a gentle low body plus a faint airy tick, low-passed.
      const lp = tapCtx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2200;
      lp.Q.value = 0.4;
      lp.connect(tapCtx.destination);
      [[520, 300, 0.05, 0.085], [1250, 900, 0.012, 0.022]].forEach(([f0, f1, peak, len]) => {
        const osc = tapCtx.createOscillator();
        const gain = tapCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f0, now);
        osc.frequency.exponentialRampToValueAtTime(f1, now + len);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(peak, now + 0.006);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + len);
        osc.connect(gain).connect(lp);
        osc.start(now);
        osc.stop(now + len + 0.02);
      });
    }
  } catch (_) { /* noop */ }
  try {
    if (localStorage.getItem("yorm_haptics_enabled") !== "false" && navigator.vibrate) navigator.vibrate(12);
  } catch (_) { /* noop */ }
}

// "Payment confirmed": two soft rising bell notes (Apple Pay feel) + a short double buzz.
// Respects the Tap sounds and haptics switches. Reuses the tap AudioContext, already unlocked by earlier taps.
export function playPaymentChime() {
  let soundOn = true;
  try { soundOn = localStorage.getItem("yorm_tap_sounds_enabled") !== "false"; } catch (_) { /* noop */ }
  try {
    const Ctx = soundOn && (window.AudioContext || window.webkitAudioContext);
    if (Ctx) {
      tapCtx = tapCtx || new Ctx();
      if (tapCtx.state === "suspended") tapCtx.resume();
      const t0 = tapCtx.currentTime + 0.02;
      const out = tapCtx.createGain();
      out.gain.value = 0.9;
      out.connect(tapCtx.destination);
      [[1318.5, 0], [1760, 0.13]].forEach(([f, at]) => {
        [[1, 0.11], [2, 0.025]].forEach(([mult, peak]) => {
          const osc = tapCtx.createOscillator();
          const g = tapCtx.createGain();
          osc.type = "sine";
          osc.frequency.value = f * mult;
          g.gain.setValueAtTime(0.0001, t0 + at);
          g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.012);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.6);
          osc.connect(g).connect(out);
          osc.start(t0 + at);
          osc.stop(t0 + at + 0.65);
        });
      });
    }
  } catch (_) { /* noop */ }
  try {
    if (localStorage.getItem("yorm_haptics_enabled") !== "false" && navigator.vibrate) navigator.vibrate([18, 70, 28]);
  } catch (_) { /* noop */ }
}

// Chat "sent": a soft rising swoosh (filtered noise sweep + faint tone) and a light tap.
// Respects the Tap sounds and haptics switches.
export function playSendSwoosh() {
  let soundOn = true;
  try { soundOn = localStorage.getItem("yorm_tap_sounds_enabled") !== "false"; } catch (_) { /* noop */ }
  try {
    const Ctx = soundOn && (window.AudioContext || window.webkitAudioContext);
    if (Ctx) {
      tapCtx = tapCtx || new Ctx();
      if (tapCtx.state === "suspended") tapCtx.resume();
      const t0 = tapCtx.currentTime + 0.01;
      const len = 0.22;
      const buf = tapCtx.createBuffer(1, Math.floor(tapCtx.sampleRate * len), tapCtx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
      const noise = tapCtx.createBufferSource();
      noise.buffer = buf;
      const bp = tapCtx.createBiquadFilter();
      bp.type = "bandpass";
      bp.Q.value = 1.4;
      bp.frequency.setValueAtTime(500, t0);
      bp.frequency.exponentialRampToValueAtTime(3200, t0 + len);
      const g = tapCtx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.09, t0 + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
      noise.connect(bp).connect(g).connect(tapCtx.destination);
      noise.start(t0);
      noise.stop(t0 + len + 0.02);
      const osc = tapCtx.createOscillator();
      const og = tapCtx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(660, t0);
      osc.frequency.exponentialRampToValueAtTime(1320, t0 + 0.16);
      og.gain.setValueAtTime(0.0001, t0);
      og.gain.exponentialRampToValueAtTime(0.03, t0 + 0.03);
      og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
      osc.connect(og).connect(tapCtx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.2);
    }
  } catch (_) { /* noop */ }
  try {
    if (localStorage.getItem("yorm_haptics_enabled") !== "false" && navigator.vibrate) navigator.vibrate(10);
  } catch (_) { /* noop */ }
}

// "Cha-ching" cash-register sound + celebratory vibration for a confirmed payment to the Pro.
// Always plays: it is the Pro's payment alert, not a tap sound.
export function playChaChing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      tapCtx = tapCtx || new Ctx();
      if (tapCtx.state === "suspended") tapCtx.resume();
      const ctx = tapCtx;
      const t0 = ctx.currentTime + 0.02;
      const out = ctx.createGain();
      out.gain.value = 0.85;
      out.connect(ctx.destination);
      // "Cha": short bright noise burst, like the register drawer.
      const len = Math.floor(ctx.sampleRate * 0.09);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 4200;
      bp.Q.value = 0.9;
      const ng = ctx.createGain();
      ng.gain.value = 0.32;
      noise.connect(bp).connect(ng).connect(out);
      noise.start(t0);
      // "Ching": metallic bell (inharmonic partials, long ring) + coin sparkle.
      const bell = (at, f, peak, decay) => {
        [[1, 1], [2.76, 0.45], [5.4, 0.22], [8.9, 0.1]].forEach(([m, w]) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = f * m;
          g.gain.setValueAtTime(0.0001, t0 + at);
          g.gain.exponentialRampToValueAtTime(peak * w, t0 + at + 0.008);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + decay);
          o.connect(g).connect(out);
          o.start(t0 + at);
          o.stop(t0 + at + decay + 0.05);
        });
      };
      bell(0.11, 1568, 0.16, 1.1);
      bell(0.12, 2093, 0.1, 0.9);
      [0.26, 0.33, 0.41, 0.47].forEach((at, i) => bell(at, 3136 + i * 260, 0.035, 0.25));
    }
  } catch (_) { /* noop */ }
  try { navigator.vibrate?.([40, 60, 40, 60, 120]); } catch (_) { /* noop */ }
}
